import React, { useState } from 'react';
import { X, Building2, User, CreditCard, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { adminApi } from '../../services/api';
import { CreateTenantRequest, TenantRecord } from '../../types';

interface CreateTenantModalProps {
  onClose: () => void;
  onSuccess: (tenant: TenantRecord) => void;
}

type FormStep = 'basic' | 'admin' | 'plan' | 'creating' | 'complete';

interface FormData {
  // Basic Info
  tenantId: string;
  displayName: string;
  
  // Admin User
  adminEmail: string;
  adminPassword: string;
  adminFirstName: string;
  adminLastName: string;
  
  // Plan & Overrides
  basePlan: string;
  applyOverrides: boolean;
  overrides: {
    maxUsers?: number;
    maxProducts?: number;
    maxConversationsPerMonth?: number;
    enableAdvancedAnalytics?: boolean;
    customBranding?: boolean;
  };
}

const CreateTenantModal: React.FC<CreateTenantModalProps> = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState<FormStep>('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdTenant, setCreatedTenant] = useState<TenantRecord | null>(null);
  
  const [formData, setFormData] = useState<FormData>({
    tenantId: '',
    displayName: '',
    adminEmail: '',
    adminPassword: '',
    adminFirstName: '',
    adminLastName: '',
    basePlan: 'starter',
    applyOverrides: false,
    overrides: {},
  });

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setError(null);
  };

  const updateOverride = (key: keyof FormData['overrides'], value: any) => {
    setFormData(prev => ({
      ...prev,
      overrides: { ...prev.overrides, [key]: value }
    }));
  };

  const validateBasicInfo = (): boolean => {
    if (!formData.tenantId.trim()) {
      setError('Tenant ID is required');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(formData.tenantId)) {
      setError('Tenant ID must contain only lowercase letters, numbers, and hyphens');
      return false;
    }
    if (!formData.displayName.trim()) {
      setError('Display name is required');
      return false;
    }
    return true;
  };

  const validateAdminInfo = (): boolean => {
    if (!formData.adminEmail.trim()) {
      setError('Admin email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      setError('Invalid email format');
      return false;
    }
    if (!formData.adminPassword || formData.adminPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (!formData.adminFirstName.trim() || !formData.adminLastName.trim()) {
      setError('Admin first and last name are required');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 'basic' && validateBasicInfo()) {
      setStep('admin');
    } else if (step === 'admin' && validateAdminInfo()) {
      setStep('plan');
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === 'admin') setStep('basic');
    else if (step === 'plan') setStep('admin');
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      setError(null);
      setStep('creating');

      const request: CreateTenantRequest = {
        tenantId: formData.tenantId,
        displayName: formData.displayName,
        adminUser: {
          email: formData.adminEmail,
          password: formData.adminPassword,
          firstName: formData.adminFirstName,
          lastName: formData.adminLastName,
        },
        basePlan: formData.basePlan,
        planOverrides: formData.applyOverrides ? formData.overrides : undefined,
      };

      const result = await adminApi.createTenant(request);
      setCreatedTenant(result);
      setStep('complete');

      // Auto-close and trigger success after 3 seconds
      setTimeout(() => {
        onSuccess(result);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create tenant');
      setStep('plan'); // Go back to plan step
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="">
      <div className="p-6">
        {/* Progress Indicator */}
        {step !== 'creating' && step !== 'complete' && (
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {['basic', 'admin', 'plan'].map((s, idx) => (
                <React.Fragment key={s}>
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step === s
                        ? 'bg-primary text-white'
                        : ['basic', 'admin', 'plan'].indexOf(step) > idx
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {idx + 1}
                    </div>
                    <span className="ml-2 text-sm font-medium dark:text-gray-300 text-gray-700 capitalize">
                      {s === 'basic' ? 'Basic Info' : s === 'admin' ? 'Admin User' : 'Plan & Overrides'}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div className={`flex-1 h-1 mx-3 rounded ${
                      ['basic', 'admin', 'plan'].indexOf(step) > idx
                        ? 'bg-green-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Basic Info Step */}
        {step === 'basic' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Create New Tenant
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Step 1: Basic Information
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                  Tenant ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.tenantId}
                  onChange={(e) => updateField('tenantId', e.target.value.toLowerCase())}
                  placeholder="acme-corp"
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                />
                <p className="mt-1 text-xs dark:text-gray-500 text-gray-600">
                  Lowercase letters, numbers, and hyphens only
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => updateField('displayName', e.target.value)}
                  placeholder="Acme Corporation"
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleNext} className="flex-1">
                Next
              </Button>
            </div>
          </>
        )}

        {/* Admin User Step */}
        {step === 'admin' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Create Admin User
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Step 2: Administrator Account
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.adminFirstName}
                    onChange={(e) => updateField('adminFirstName', e.target.value)}
                    placeholder="John"
                    className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.adminLastName}
                    onChange={(e) => updateField('adminLastName', e.target.value)}
                    placeholder="Doe"
                    className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => updateField('adminEmail', e.target.value)}
                  placeholder="admin@acme.com"
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={formData.adminPassword}
                  onChange={(e) => updateField('adminPassword', e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleBack} className="flex-1">
                Back
              </Button>
              <Button variant="primary" onClick={handleNext} className="flex-1">
                Next
              </Button>
            </div>
          </>
        )}

        {/* Plan & Overrides Step */}
        {step === 'plan' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <CreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Subscription Plan
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Step 3: Plan & Custom Overrides
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                  Base Plan <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.basePlan}
                  onChange={(e) => updateField('basePlan', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="starter">Starter</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.applyOverrides}
                  onChange={(e) => updateField('applyOverrides', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm dark:text-gray-300 text-gray-700">
                  Apply custom plan overrides
                </span>
              </label>

              {formData.applyOverrides && (
                <div className="p-4 rounded-lg border dark:border-gray-700 border-gray-200 space-y-3">
                  <p className="text-xs font-medium dark:text-gray-400 text-gray-600 uppercase tracking-wide">
                    Custom Limits
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm dark:text-gray-300 text-gray-700 mb-1">
                        Max Users
                      </label>
                      <input
                        type="number"
                        value={formData.overrides.maxUsers || ''}
                        onChange={(e) => updateOverride('maxUsers', parseInt(e.target.value) || undefined)}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm dark:text-gray-300 text-gray-700 mb-1">
                        Max Products
                      </label>
                      <input
                        type="number"
                        value={formData.overrides.maxProducts || ''}
                        onChange={(e) => updateOverride('maxProducts', parseInt(e.target.value) || undefined)}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm dark:text-gray-300 text-gray-700 mb-1">
                        Max Conversations/Month
                      </label>
                      <input
                        type="number"
                        value={formData.overrides.maxConversationsPerMonth || ''}
                        onChange={(e) => updateOverride('maxConversationsPerMonth', parseInt(e.target.value) || undefined)}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 text-sm"
                      />
                    </div>
                  </div>

                  <p className="text-xs font-medium dark:text-gray-400 text-gray-600 uppercase tracking-wide mt-4">
                    Feature Flags
                  </p>

                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.overrides.enableAdvancedAnalytics || false}
                        onChange={(e) => updateOverride('enableAdvancedAnalytics', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm dark:text-gray-300 text-gray-700">
                        Enable Advanced Analytics
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.overrides.customBranding || false}
                        onChange={(e) => updateOverride('customBranding', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm dark:text-gray-300 text-gray-700">
                        Enable Custom Branding
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleBack} className="flex-1">
                Back
              </Button>
              <Button variant="primary" onClick={handleCreate} className="flex-1" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Tenant'
                )}
              </Button>
            </div>
          </>
        )}

        {/* Creating Progress */}
        {step === 'creating' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Creating Tenant...
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Please wait while we set up everything
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {[
                'Setting up tenant database',
                'Creating admin user',
                'Applying plan configuration',
                'Initializing Stripe customer',
                'Finalizing setup'
              ].map((stepName, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 border-gray-200">
                  <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                  <span className="text-sm dark:text-gray-300 text-gray-700">{stepName}...</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Complete Step */}
        {step === 'complete' && createdTenant && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Tenant Created Successfully!
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  {createdTenant.name} is ready to use
                </p>
              </div>
            </div>

            <div className="p-4 rounded-lg border dark:border-gray-700 border-gray-200 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs dark:text-gray-500 text-gray-600 uppercase tracking-wide">
                    Tenant ID
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 font-mono mt-1">
                    {createdTenant.tenantId}
                  </p>
                </div>
                <div>
                  <label className="text-xs dark:text-gray-500 text-gray-600 uppercase tracking-wide">
                    Display Name
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 mt-1">
                    {createdTenant.name}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-center dark:text-gray-400 text-gray-600 mb-4">
              Closing automatically in 3 seconds...
            </p>

            <Button onClick={() => onSuccess(createdTenant)} className="w-full">
              Done
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CreateTenantModal;
